/* eslint-disable no-console */
import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { lineLotApi } from 'services/apiDigitalTicket/lineLot';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';

import * as LineLotActions from './actions';
import { LineLotTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof LineLotActions.listLineLotRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando lotes' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(lineLotApi.list, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        LineLotActions.listLineLotSuccess({
          lineLotList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('line lot - listLine Lot - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LineLotActions.listLineLotFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof LineLotActions.getLineLotRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const { params } = payload;
    const transportOperatorSelected = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(lineLotApi.get, {
      params: {
        transportOperatorId: transportOperatorSelected.id,
        ...params,
      },
    });
    if (response.status === 200) {
      yield put(
        LineLotActions.getLineLotSuccess({
          lineLotData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('line lot - getLine Lot - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LineLotActions.getLineLotFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof LineLotActions.filterLineLotsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(lineLotApi.list, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(LineLotActions.filterLineLotsSuccess());
      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('line lot - filterLineLots - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LineLotActions.filterLineLotsFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* create({
  payload,
}: ReturnType<typeof LineLotActions.createLineLotRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando lote' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(lineLotApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        LineLotActions.createLineLotSuccess({ lineLotData: response.data }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Lote criado com sucesso',
        }),
      );
      yield put(
        LineLotActions.listLineLotRequest({
          _includes: 'line',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('line lot - createLot - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LineLotActions.createLineLotFailure());
    yield put(DialogsActions.setClose());
  }
}

function* addLineLot({
  payload,
}: ReturnType<typeof LineLotActions.addLineLotRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Adicionando linha para o lote' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(lineLotApi.addLines, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        LineLotActions.addLineLotSuccess({ lineLotAdded: response.data }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Linha(s) adicionada(s) com sucesso',
        }),
      );
      yield put(
        LineLotActions.listLineLotRequest({
          _includes: 'line',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('add line lot - createLot - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LineLotActions.addLineLotFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof LineLotActions.updateLineLotRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando lote' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(lineLotApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(LineLotActions.updateLineLotSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Lote alterado com sucesso',
        }),
      );
      yield put(
        LineLotActions.listLineLotRequest({
          _includes: 'line',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lots - updateLot - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LineLotActions.updateLineLotFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof LineLotActions.deleteLineLotRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo lote' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(lineLotApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(LineLotActions.deleteLineLotSuccess());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Lote excluído com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        LineLotActions.listLineLotRequest({
          _includes: 'line',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('line lot - remove lote - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LineLotActions.deleteLineLotFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const lineLotSagas = [
  takeLatest(LineLotTypes.LIST_LINE_LOT_REQUEST, list),
  takeLatest(LineLotTypes.GET_LINE_LOT_REQUEST, get),
  takeLatest(LineLotTypes.FILTER_LINE_LOTS_REQUEST, filter),
  takeLatest(LineLotTypes.CREATE_LINE_LOT_REQUEST, create),
  takeLatest(LineLotTypes.ADD_LINE_LOT_REQUEST, addLineLot),
  takeLatest(LineLotTypes.UPDATE_LINE_LOT_REQUEST, update),
  takeLatest(LineLotTypes.DELETE_LINE_LOT_REQUEST, remove),
];
